<template>
  <!-- Postfächer der Abteilungen (Supportanfrage Klinger & Kollegen,
       01.10.2026): „Die Einladungen sollten an gutachten@… gehen, das hat
       Stefan schon in den Stammdaten hinterlegt, allerdings kommen die Mails
       weiter an info@…" Niemand sah, welche Adresse am Ende greift – hier
       steht sie je Abteilung samt Herkunft. Geändert wird in „Postfächer bearbeiten“
       (Kundenfeedback KS 29.10.2026, I). -->
  <section class="dm" data-testid="department-mailboxes">
    <h3 class="dm__title">{{ $gt('Postfächer Ihrer Abteilungen') }}</h3>
    <!-- Kundenfeedback 16.10.2026 (VIII): „An ‚ihr' Postfach?! Also an meins?"
         – Bezug ausdrücklich benannt und die Bedingung (noch kein
         Sachbearbeiter) genannt, die im Digest tatsächlich gilt. -->
    <p class="dm__hint">
      {{ $gt('Jede Abteilung kann eine eigene E-Mail-Adresse haben, ihr Postfach. Ist eine Abteilung an einem Fall beteiligt, dem noch kein Sachbearbeiter zugeordnet ist, schickt KARS die Benachrichtigungen zu diesem Fall an das Postfach der Abteilung. Hat die Abteilung keine eigene Adresse, verwendet KARS die des Standorts, sonst die des Unternehmens.') }}
    </p>

    <p v-if="mailboxes.length === 0" class="dm__empty">
      {{ $gt('In Ihrer Organisation sind noch keine Abteilungen angelegt.') }}
    </p>
    <!-- Kundenfeedback 16.10.2026 (VII): Bei langen Abteilungsnamen rutschte
         die Adresse unter den Namen nach links. Jetzt festes Raster: links
         Abteilung (darunter Standort), rechts Adresse (darunter Herkunft);
         zu lange Adressen werden gekürzt und stehen vollständig im Tooltip.
         Ist die Liste sehr schmal, stehen alle Zeilen einheitlich untereinander. -->
    <ul v-else class="dm__list">
      <li v-for="m in mailboxes" :key="m.idDepartment" class="dm__row" :class="{ 'is-empty': !m.email }">
        <span class="dm__place">
          <span class="dm__department">{{ m.department || mailboxPlace(m) }}</span>
          <span v-if="m.department && m.location" class="dm__location">{{ m.location }}</span>
        </span>
        <span class="dm__address">
          <span class="dm__email" :title="m.email || undefined">{{ m.email || '–' }}</span>
          <span class="dm__source">{{ $gt(mailboxSourceLabel(m.source)) }}</span>
        </span>
      </li>
    </ul>

    <button
      v-if="editable"
      class="dm__link"
      data-testid="mailboxes-edit"
      type="button"
      @click="$emit('edit')"
    >
      <Pencil :size="13" />
      {{ $gt('Postfächer bearbeiten') }}
    </button>
  </section>
</template>

<script setup lang="ts">
  import { Pencil } from 'lucide-vue-next'
  import { type DepartmentMailbox, mailboxPlace, mailboxSourceLabel } from './departmentMailboxModel'

  withDefaults(defineProps<{ mailboxes: DepartmentMailbox[], editable?: boolean }>(), { editable: true })

  defineEmits<{ (e: 'edit'): void }>()
</script>

<style lang="scss" scoped>
.dm { margin-top: 18px; }
.dm__title {
  font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.07em; color: #475569; margin: 0 0 3px;
}
.dm__hint { font-size: 0.76rem; color: #94a3b8; line-height: 1.45; margin: 0 0 10px; }
.dm__empty {
  margin: 0; padding: 14px; text-align: center;
  font-size: 0.8rem; color: #94a3b8; border: 1px dashed #e2e8f0; border-radius: 10px;
}

.dm__list {
  list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px;
  container-type: inline-size;
}
.dm__row {
  display: grid; align-items: center; column-gap: 14px; row-gap: 4px;
  grid-template-columns: minmax(7.5rem, 1fr) minmax(0, auto);
  padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff;
  &.is-empty { border-style: dashed; background: #f8fafc; }
}
.dm__place, .dm__address { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.dm__department { font-size: 0.84rem; font-weight: 600; color: #0f172a; overflow-wrap: anywhere; line-height: 1.3; }
.dm__location { font-size: 0.7rem; color: #64748b; }
.dm__address { align-items: flex-end; text-align: right; }
.dm__email {
  max-width: 100%;
  font-size: 0.8rem; color: #0b5cad; font-variant-numeric: tabular-nums;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  .is-empty & { color: #94a3b8; }
}
.dm__source { font-size: 0.7rem; color: #64748b; }

// Sehr schmal (Handy): konsequent zweizeilig für alle Zeilen gleich.
@container (max-width: 340px) {
  .dm__row { grid-template-columns: minmax(0, 1fr); }
  .dm__address { align-items: flex-start; text-align: left; }
}

.dm__link {
  display: inline-flex; align-items: center; gap: 5px;
  margin-top: 10px; padding: 4px 9px; border-radius: 7px; cursor: pointer;
  border: 1px solid #bfdbfe; background: #eff6ff; color: #1d4ed8;
  font-size: 0.74rem; font-weight: 600;
  transition: background-color .15s, border-color .15s;
  &:hover { background: #dbeafe; border-color: #93c5fd; }
  &:focus-visible { outline: 2px solid #1d4ed8; outline-offset: 2px; }
}
</style>
